import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { 
  Plus, 
  MapPin,
  Globe,
  CreditCard,
  Loader2,
  Building2,
  FileText,
  Check,
  ChevronRight,
  ChevronDown,
  ArrowLeftRight,
  Tag
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/api";
import { useToast } from "../hooks/use-toast";
// StripePaymentElement removed - Brightstone internal use only
import { useAuth } from "../contexts/AuthContext";

const ADDITIONAL_SOURCES = [
  { id: "provincial", name: "Provincial Data Feeds", type: "Provincial", status: "Active", icon: Globe },
  { id: "municipal", name: "Municipal Agendas & Minutes", type: "Municipal", status: "Active", icon: FileText },
  { id: "regional", name: "Regional Agendas & Minutes", type: "Regional", status: "Active", icon: FileText },
];

export default function Sources() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Fetch sources/municipalities - use listAll to get both explicit and implicit (via properties) monitored municipalities
  const { data: sourcesData, isLoading } = useQuery({
    queryKey: ['sources'],
    queryFn: () => apiClient.companyMunicipalities.listAll(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    // Use default refetchOnMount: false (from queryClient defaults)
    // Use default refetchOnWindowFocus: false (from queryClient defaults)
  });
  
  // Fetch all properties on component load - this ensures properties are always available
  const { data: allPropertiesData } = useQuery({
    queryKey: ['all-properties'],
    queryFn: () => apiClient.properties.list(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    // Use default refetchOnMount: false (from queryClient defaults)
    // Use default refetchOnWindowFocus: false (from queryClient defaults)
  });

  // Fetch all categories/topics to show which ones monitor each municipality
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.topics.list(),
    staleTime: 5 * 60 * 1000,
  });
  
  // Group properties by municipality_id
  const propertiesByMunicipality = useMemo(() => {
    if (!allPropertiesData || !Array.isArray(allPropertiesData)) {
      return {};
    }
    
    const grouped: Record<number, any[]> = {};
    allPropertiesData.forEach((prop: any) => {
      const municipalityId = prop.municipality_id;
      if (municipalityId) {
        if (!grouped[municipalityId]) {
          grouped[municipalityId] = [];
        }
        grouped[municipalityId].push(prop);
      }
    });
    
    return grouped;
  }, [allPropertiesData]);
  
  // State to track expanded municipalities
  const [expandedMunicipalities, setExpandedMunicipalities] = useState<Set<number>>(new Set());
  
  // State for Add Municipality dialog
  const [isAddMunicipalityOpen, setIsAddMunicipalityOpen] = useState(false);
  const [addStep, setAddStep] = useState<"select" | "confirm" | "success">("select");
  const [selectedMunicipalityIds, setSelectedMunicipalityIds] = useState<Set<number>>(new Set());
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // Payment method form removed - Brightstone internal use only
  const [municipalityDropdownOpen, setMunicipalityDropdownOpen] = useState(false);
  
  // State for Switch Municipality dialog
  const [isSwitchDialogOpen, setIsSwitchDialogOpen] = useState(false);
  const [switchFromMunicipality, setSwitchFromMunicipality] = useState<any | null>(null);
  const [switchToMunicipalityId, setSwitchToMunicipalityId] = useState<number | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  
  // State for Custom Category Monitoring Confirmation dialog
  const [isCustomCategoryDialogOpen, setIsCustomCategoryDialogOpen] = useState(false);
  const [pendingCustomCategories, setPendingCustomCategories] = useState<Array<{id: number, display_name: string, slug: string}>>([]);
  const [selectedCustomCategoryIds, setSelectedCustomCategoryIds] = useState<Set<number>>(new Set());
  const [newMunicipalityForCategories, setNewMunicipalityForCategories] = useState<{id: number, name: string} | null>(null);
  const [isAddingCategoryMonitoring, setIsAddingCategoryMonitoring] = useState(false);
  
  // Subscription tier and limits
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  // Fetch company info to get subscription tier
  const { data: companyInfo } = useQuery({
    queryKey: ['company-info'],
    queryFn: () => apiClient.companies.getInfo(),
  });
  
  useEffect(() => {
    if (companyInfo) {
      setSubscriptionTier(companyInfo.subscription_tier || null);
    }
  }, [companyInfo]);
  
  // Fetch all municipalities from the municipalities table (not just monitored ones)
  // Filter by source_type = 'municipal' when adding a municipality
  // Use public endpoint to get ALL municipalities, not just monitored ones
  const { data: allMunicipalities, isLoading: isLoadingMunicipalities } = useQuery({
    queryKey: ['all-municipalities', 'municipal'],
    queryFn: async () => {
      const data = await apiClient.public.getMunicipalities('municipal');
      console.log('[Sources] Fetched municipalities from API:', data?.length || 0, data);
      return data;
    },
  });
  
  // Get list of already monitored municipality IDs
  const monitoredMunicipalityIds = useMemo(() => {
    if (!sourcesData) return new Set<number>();
    const monitored = sourcesData.map((mun: any) => mun.municipality_id || mun.id);
    console.log('[Sources] Monitored municipality IDs from sourcesData:', Array.from(new Set(monitored)));
    console.log('[Sources] SourcesData:', sourcesData);
    return new Set(monitored);
  }, [sourcesData]);
  
  // Filter available municipalities (exclude already monitored ones)
  // Note: Backend already filters by source_type = 'municipal', so we only need to exclude monitored ones
  const availableMunicipalities = useMemo(() => {
    if (!allMunicipalities || !Array.isArray(allMunicipalities)) {
      console.log('[Sources] No allMunicipalities data or not an array');
      return [];
    }
    
    console.log('[Sources] Total municipalities from API:', allMunicipalities.length);
    console.log('[Sources] All municipalities:', allMunicipalities.map((m: any) => ({ id: m.id, name: m.name })));
    console.log('[Sources] Monitored IDs:', Array.from(monitoredMunicipalityIds));
    
    const available = allMunicipalities.filter((mun: any) => {
      const id = mun.id;
      const isMonitored = monitoredMunicipalityIds.has(id);
      // Exclude already monitored municipalities
      return !isMonitored;
    });
    
    console.log('[Sources] Available municipalities after filtering:', available.length);
    console.log('[Sources] Available municipalities:', available.map((m: any) => ({ id: m.id, name: m.name })));
    return available;
  }, [allMunicipalities, monitoredMunicipalityIds]);

  // Get selected municipality objects
  const selectedMunicipalitiesList = useMemo(() => {
    return availableMunicipalities.filter((mun: any) => selectedMunicipalityIds.has(mun.id));
  }, [selectedMunicipalityIds, availableMunicipalities]);

  // Map categories to municipalities - which topics monitor which municipalities
  const categoriesByMunicipality = useMemo(() => {
    if (!categoriesData || !Array.isArray(categoriesData)) {
      return {};
    }
    
    const mapping: Record<number, any[]> = {};
    categoriesData.forEach((category: any) => {
      // Check if category has monitored_sources or sources array
      const sources = category.monitored_sources || category.sources || [];
      sources.forEach((source: any) => {
        // For municipal sources, use municipality_id
        if (source.source_type === 'municipal' && source.municipality_id) {
          const munId = source.municipality_id;
          if (!mapping[munId]) {
            mapping[munId] = [];
          }
          // Avoid duplicates
          if (!mapping[munId].find((c: any) => c.id === category.id)) {
            mapping[munId].push(category);
          }
        }
      });
    });
    
    return mapping;
  }, [categoriesData]);

  // Transform municipality data to expected format (needed for limit calculations)
  const municipalities = sourcesData ? sourcesData.map((mun: any) => {
    const municipalityId = mun.municipality_id || mun.id;
    const propertiesList = propertiesByMunicipality[municipalityId] || [];
    const propertyCount = propertiesList.length || mun.property_count || 0;
    const monitoringTopics = categoriesByMunicipality[municipalityId] || [];
    
    return {
      id: municipalityId,
      name: mun.municipality_name || mun.name,
      properties: propertyCount,
      status: mun.is_active !== false ? "Active" : "Inactive",
      enabled: mun.is_active !== false,
      propertiesList: propertiesList,
      monitoringTopics: monitoringTopics
    };
  }) : [];

  // Calculate max municipalities based on subscription tier (after municipalities is defined)
  // Default to 10 (org plan) if subscriptionTier is null or not 'solo'
  const maxMunicipalities = subscriptionTier === 'solo' ? 5 : 10;
  const currentMunicipalityCount = municipalities.length;
  // Allow adding if we don't have subscription tier yet, or if we're below the limit
  const canAddMore = subscriptionTier === null || currentMunicipalityCount < maxMunicipalities;
  const remainingSlots = Math.max(0, maxMunicipalities - currentMunicipalityCount);

  // Fetch payment method
  // Payment method query removed - Brightstone internal use only

  // Fetch municipality price
  const { data: municipalityPriceData } = useQuery({
    queryKey: ['municipality-price'],
    queryFn: () => apiClient.stripe.getMunicipalityPrice(),
    enabled: addStep === "confirm", // Only fetch when on confirm step
  });

  // Fetch trial status (for both confirm and success steps)
  const { data: trialStatus } = useQuery({
    queryKey: ['trial-status'],
    queryFn: () => apiClient.auth.getTrialStatus(),
    enabled: addStep === "confirm" || addStep === "success", // Fetch when on confirm or success step
  });
  
  // Handle success redirect from Stripe checkout
  useEffect(() => {
    const success = searchParams.get('success');
    const municipalityId = searchParams.get('municipality_id');
    
    if (success === 'true' && municipalityId) {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['all-properties'] });
      toast({
        title: "Payment successful!",
        description: "The municipality has been added to your monitoring list",
      });
      // Clean up URL params
      setSearchParams({});
    }
  }, [searchParams, queryClient, toast, setSearchParams]);
  
  // Handle municipality selection/deselection from dropdown
  const handleMunicipalityToggle = (municipalityId: number) => {
    const isCurrentlySelected = selectedMunicipalityIds.has(municipalityId);
    
    if (isCurrentlySelected) {
      // Deselect
      const newSelectedIds = new Set(selectedMunicipalityIds);
      newSelectedIds.delete(municipalityId);
      setSelectedMunicipalityIds(newSelectedIds);
      
      // Update selected municipalities list
      setSelectedMunicipalities((prev) => prev.filter((m: any) => m.id !== municipalityId));
    } else {
      // Check if we can add more
      const currentMax = subscriptionTier === 'solo' ? 5 : 10;
      const currentCount = municipalities.length;
      const newCount = currentCount + selectedMunicipalityIds.size + 1;
      
      if (subscriptionTier && newCount > currentMax) {
        if (subscriptionTier === 'solo') {
          setShowUpgradeDialog(true);
        } else {
          toast({
            title: "Limit reached",
            description: `You can monitor up to ${currentMax} municipalities on the Org plan.`,
            variant: "destructive",
          });
        }
        return;
      }
      
      // Select
      const newSelectedIds = new Set(selectedMunicipalityIds);
      newSelectedIds.add(municipalityId);
      setSelectedMunicipalityIds(newSelectedIds);
      
      // Update selected municipalities list
      const municipality = availableMunicipalities.find((mun: any) => mun.id === municipalityId);
      if (municipality) {
        setSelectedMunicipalities((prev) => [...prev, municipality]);
      }
    }
  };
  
  // Sync selectedMunicipalities with selectedMunicipalityIds
  useEffect(() => {
    const selected = availableMunicipalities.filter((mun: any) => selectedMunicipalityIds.has(mun.id));
    setSelectedMunicipalities(selected);
  }, [selectedMunicipalityIds, availableMunicipalities]);
  
  // Handle upgrade to org plan
  const handleUpgradeToOrg = async () => {
    setIsUpgrading(true);
    try {
      await apiClient.stripe.upgradeToOrgPlan();
      toast({
        title: "Upgrade successful!",
        description: "Your subscription has been upgraded to Org plan. You can now monitor up to 10 municipalities.",
      });
      // Refresh company info to update subscription tier
      queryClient.invalidateQueries({ queryKey: ['company-info'] });
      setShowUpgradeDialog(false);
    } catch (error: any) {
      console.error('Failed to upgrade subscription:', error);
      toast({
        title: "Upgrade failed",
        description: error?.response?.data?.detail || "Failed to upgrade subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpgrading(false);
    }
  };
  

  // Subscribe to municipalities mutation
  const subscribeMutation = useMutation({
    mutationFn: (municipalityIds: number[]) => apiClient.stripe.subscribeToMunicipalities(municipalityIds),
    onSuccess: (data) => {
      setIsProcessing(false);
      setAddStep("success");
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['all-properties'] });
      toast({
        title: "Success!",
        description: `Successfully added ${data.subscriptions.length} municipality(ies) to your monitoring list`,
      });
    },
    onError: (error: any) => {
      setIsProcessing(false);
      toast({
        title: "Error",
        description: error?.response?.data?.detail || "Failed to subscribe to municipalities",
        variant: "destructive",
      });
    },
  });

  const handleConfirmSubscription = () => {
    if (selectedMunicipalities.length === 0) {
      toast({
        title: "Please select municipalities",
        description: "You must select at least one municipality",
        variant: "destructive",
      });
      return;
    }
    
    // Recalculate values to ensure we have the latest subscription tier
    const currentMax = subscriptionTier === 'solo' ? 5 : 10;
    const currentCount = municipalities.length;
    const newCount = currentCount + selectedMunicipalities.length;
    
    // Check if adding these municipalities would exceed the limit
    if (subscriptionTier && newCount > currentMax) {
      if (subscriptionTier === 'solo') {
        setShowUpgradeDialog(true);
      } else {
        toast({
          title: "Limit reached",
          description: `You can monitor up to ${currentMax} municipalities on the Org plan.`,
          variant: "destructive",
        });
      }
      return;
    }
    
    setIsProcessing(true);
    const municipalityIds = selectedMunicipalities.map((m: any) => m.id);
    subscribeMutation.mutate(municipalityIds);
  };
  
  const handleAddMunicipalityClick = () => {
    // Recalculate values to ensure we have the latest subscription tier
    const currentMax = subscriptionTier === 'solo' ? 5 : 10;
    const currentCount = municipalities.length;
    
    // Check if we're at the limit before opening the dialog
    // Only check if subscriptionTier is loaded and we're actually at the limit
    if (subscriptionTier && currentCount >= currentMax) {
      if (subscriptionTier === 'solo') {
        setShowUpgradeDialog(true);
      } else {
        toast({
          title: "Limit reached",
          description: `You can monitor up to ${currentMax} municipalities on the ${subscriptionTier === 'solo' ? 'Solo' : 'Org'} plan.`,
          variant: "destructive",
        });
      }
      return;
    }
    setIsAddMunicipalityOpen(true);
  };

  // Attach payment method mutation
  // Payment method mutation removed - Brightstone internal use only

  // Payment method handler removed - Brightstone internal use only

  const resetModal = () => {
    setIsAddMunicipalityOpen(false);
    setShowPaymentMethodForm(false);
    setTimeout(() => {
      setAddStep("select");
      setSelectedMunicipalities([]);
      setSelectedMunicipalityIds(new Set());
      setMunicipalityDropdownOpen(false);
    }, 300);
  };

  // Switch municipality mutation
  const switchMutation = useMutation({
    mutationFn: ({ oldId, newId }: { oldId: number; newId: number }) => 
      apiClient.companyMunicipalities.switch(oldId, newId),
    onSuccess: (data) => {
      setIsSwitching(false);
      setIsSwitchDialogOpen(false);
      setSwitchFromMunicipality(null);
      setSwitchToMunicipalityId(null);
      
      // Invalidate all relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['all-properties'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['pulses'] });
      queryClient.invalidateQueries({ queryKey: ['company-municipalities'] });
      
      // Check if there are custom categories that need user confirmation
      if (data.pending_custom_categories && data.pending_custom_categories.length > 0) {
        setPendingCustomCategories(data.pending_custom_categories);
        setNewMunicipalityForCategories(data.new_municipality);
        setSelectedCustomCategoryIds(new Set()); // Start with none selected
        setIsCustomCategoryDialogOpen(true);
        
        toast({
          title: "Municipality Switched!",
          description: `Switched to ${data.new_municipality?.name}. You have custom topics that may need updating.`,
        });
      } else {
        toast({
          title: "Municipality Switched!",
          description: data.message || `Switched to ${data.new_municipality?.name}`,
        });
      }
    },
    onError: (error: any) => {
      setIsSwitching(false);
      toast({
        title: "Switch Failed",
        description: error?.response?.data?.detail || "Failed to switch municipality",
        variant: "destructive",
      });
    },
  });

  // Handle switch button click (opens dialog with pre-selected municipality)
  const handleSwitchClick = (municipality: any) => {
    setSwitchFromMunicipality(municipality);
    setSwitchToMunicipalityId(null);
    setIsSwitchDialogOpen(true);
  };

  // Handle switch button click from header (no pre-selection)
  const handleSwitchFromHeaderClick = () => {
    setSwitchFromMunicipality(null);
    setSwitchToMunicipalityId(null);
    setIsSwitchDialogOpen(true);
  };

  // Perform the switch
  const handleConfirmSwitch = () => {
    if (!switchFromMunicipality || !switchToMunicipalityId) {
      toast({
        title: "Selection Required",
        description: "Please select both a municipality to remove and one to add",
        variant: "destructive",
      });
      return;
    }
    
    setIsSwitching(true);
    switchMutation.mutate({
      oldId: switchFromMunicipality.id,
      newId: switchToMunicipalityId
    });
  };

  // Reset switch dialog
  const resetSwitchDialog = () => {
    setIsSwitchDialogOpen(false);
    setTimeout(() => {
      setSwitchFromMunicipality(null);
      setSwitchToMunicipalityId(null);
    }, 300);
  };

  // Handle custom category monitoring confirmation
  const handleConfirmCategoryMonitoring = async () => {
    if (!newMunicipalityForCategories) return;
    
    setIsAddingCategoryMonitoring(true);
    try {
      const selectedIds = Array.from(selectedCustomCategoryIds);
      if (selectedIds.length > 0) {
        await apiClient.companyMunicipalities.addCategoryMonitoring(
          newMunicipalityForCategories.id,
          selectedIds
        );
        toast({
          title: "Topics Updated!",
          description: `${selectedIds.length} custom topic(s) will now monitor ${newMunicipalityForCategories.name}`,
        });
      }
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (error: any) {
      toast({
        title: "Update Failed",
        description: error?.response?.data?.detail || "Failed to update topic monitoring",
        variant: "destructive",
      });
    } finally {
      setIsAddingCategoryMonitoring(false);
      setIsCustomCategoryDialogOpen(false);
      setPendingCustomCategories([]);
      setSelectedCustomCategoryIds(new Set());
      setNewMunicipalityForCategories(null);
    }
  };

  // Toggle custom category selection
  const toggleCustomCategorySelection = (categoryId: number) => {
    setSelectedCustomCategoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Select all custom categories
  const selectAllCustomCategories = () => {
    setSelectedCustomCategoryIds(new Set(pendingCustomCategories.map(c => c.id)));
  };

  // Deselect all custom categories
  const deselectAllCustomCategories = () => {
    setSelectedCustomCategoryIds(new Set());
  };

  // Handle accordion expansion
  const handleAccordionChange = (municipalityId: string) => {
    const id = parseInt(municipalityId);
    if (!isNaN(id)) {
      if (expandedMunicipalities.has(id)) {
        setExpandedMunicipalities(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      } else {
        setExpandedMunicipalities(prev => {
          const newSet = new Set(prev);
          newSet.add(id);
          return newSet;
        });
      }
    }
  };
  
  const activeCount = municipalities.filter((m: any) => m.enabled).length;

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-[1600px] mx-auto space-y-8">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight">Sources</h1>
              <p className="text-muted-foreground mt-1">
                Manage your monitoring subscriptions and data feeds.
              </p>
            </div>
            <div className="flex items-center gap-3">
               <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 px-3 py-1.5 rounded-full text-xs font-medium text-neutral-600 dark:text-neutral-400 shadow-sm">
                  <span className="text-primary font-bold">{activeCount}</span> Municipalities Active
               </div>
            </div>
          </div>

          {/* Municipalities Section */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-foreground">Municipalities</h2>
                {subscriptionTier && (
                  <span className="text-xs text-neutral-500">
                    ({currentMunicipalityCount}/{maxMunicipalities} {subscriptionTier === 'solo' ? 'Solo plan limit' : 'Org plan limit'})
                  </span>
                )}
              </div>
              {canAddMore ? (
                <Button 
                  className="h-9 text-xs bg-primary hover:bg-primary/90 text-white shadow-sm gap-2"
                  onClick={handleAddMunicipalityClick}
                >
                  <Plus className="h-4 w-4" /> Add Municipality
                </Button>
              ) : (
                <Button 
                  className="h-9 text-xs bg-primary hover:bg-primary/90 text-white shadow-sm gap-2"
                  onClick={handleSwitchFromHeaderClick}
                >
                  <ArrowLeftRight className="h-4 w-4" /> Switch Municipality
                </Button>
              )}
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm overflow-hidden">
              <Accordion type="single" collapsible className="w-full" onValueChange={handleAccordionChange}>
                {municipalities.length > 0 ? municipalities.map((city: any) => (
                  <AccordionItem key={city.id} value={String(city.id)} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                    <AccordionTrigger className="px-6 py-4 hover:bg-neutral-50/50 dark:hover:bg-neutral-900/50 transition-colors">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-500">
                            <MapPin className="h-5 w-5" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">{city.name}</div>
                            <div className="text-xs text-neutral-500 flex items-center gap-1">
                              <span className={cn("h-1.5 w-1.5 rounded-full", city.enabled ? "bg-green-500" : "bg-neutral-400")} />
                              {city.status}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* Switch button - only show if no properties */}
                          {city.properties === 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs text-neutral-500 hover:text-primary hover:bg-primary/10 gap-1.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSwitchClick(city);
                              }}
                              title="Switch this municipality for another"
                            >
                              <ArrowLeftRight className="h-3.5 w-3.5" />
                              Switch
                            </Button>
                          )}
                          <div className="text-xs font-medium text-neutral-500 bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1 rounded-md">
                            {city.properties} {city.properties === 1 ? 'property' : 'properties'}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6 pt-2 bg-neutral-50/30 dark:bg-neutral-900/30">
                      <div className="pl-[56px] space-y-3">
                        {/* Topics Monitoring Section */}
                        {city.monitoringTopics && city.monitoringTopics.length > 0 ? (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Monitoring Topics</span>
                            </div>
                            <div className="space-y-2">
                              {city.monitoringTopics.map((topic: any) => (
                                <div key={topic.id} className="flex items-center gap-2 bg-white dark:bg-neutral-950 p-2.5 rounded-md border border-neutral-200 dark:border-neutral-800">
                                  <Tag className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{topic.display_name || topic.name || topic.slug}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : expandedMunicipalities.has(city.id) ? (
                          <div className="text-center py-4 text-sm text-neutral-500">
                            <Tag className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                            No topics monitoring this municipality
                          </div>
                        ) : null}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )) : (
                  <div className="p-8 text-center text-neutral-500">
                    <MapPin className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
                    <p>No municipalities configured yet</p>
                    <p className="text-sm mt-1">Add a municipality to start monitoring</p>
                  </div>
                )}
              </Accordion>
            </div>
          </section>

          {/* Additional Sources Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-bold text-foreground">Additional Sources</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {ADDITIONAL_SOURCES.map((source) => {
                const IconComponent = source.icon;
                const hasMunicipalities = municipalities.length > 0;
                const isActive = hasMunicipalities;
                return (
                  <div key={source.id} className="bg-white dark:bg-neutral-900 p-5 rounded-xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm flex items-center justify-between group hover:border-primary/30 transition-colors">
                     <div className="flex items-center gap-4">
                       <div className={cn(
                         "h-10 w-10 rounded-full flex items-center justify-center",
                         isActive ? "bg-[#F1F8E9] dark:bg-primary/10 text-primary" : "bg-neutral-100 dark:bg-neutral-800 text-neutral-400"
                       )}>
                         <IconComponent className="h-5 w-5" />
                       </div>
                       <div>
                         <div className="font-semibold text-sm text-neutral-900 dark:text-neutral-100">{source.name}</div>
                         <div className="text-xs text-neutral-500">{source.type}</div>
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                       <Badge variant="outline" className={cn(
                         "text-[10px]",
                         isActive 
                           ? "bg-green-50 text-green-600 border-green-200" 
                           : "bg-red-50 text-red-600 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800"
                       )}>
                         {isActive ? "Active" : "Inactive"}
                       </Badge>
                     </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Topics Section */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">Topics</h2>
              <Button 
                variant="outline"
                size="sm"
                className="h-9 text-xs gap-2"
                onClick={() => navigate('/dashboard')}
              >
                <Tag className="h-3.5 w-3.5" /> Manage Topics
              </Button>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm overflow-hidden">
              {categoriesData && Array.isArray(categoriesData) && categoriesData.length > 0 ? (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {categoriesData.map((category: any) => {
                    const monitoredSources = category.monitored_sources || category.sources || [];
                    const municipalSources = monitoredSources.filter((s: any) => s.source_type === 'municipal');
                    const hasMunicipalMonitoring = municipalSources.length > 0;
                    
                    return (
                      <div key={category.id} className="p-4 hover:bg-neutral-50/50 dark:hover:bg-neutral-900/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Tag className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-semibold text-sm text-neutral-900 dark:text-neutral-100">
                                {category.display_name || category.name || category.slug}
                              </div>
                              <div className="text-xs text-neutral-500">
                                {hasMunicipalMonitoring 
                                  ? `Monitoring ${municipalSources.length} municipalit${municipalSources.length === 1 ? 'y' : 'ies'}`
                                  : 'No municipal monitoring'}
                              </div>
                            </div>
                          </div>
                          <Badge variant="outline" className={cn(
                            "text-[10px]",
                            hasMunicipalMonitoring
                              ? "bg-green-50 text-green-600 border-green-200"
                              : "bg-neutral-50 text-neutral-500 border-neutral-200"
                          )}>
                            {hasMunicipalMonitoring ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        {hasMunicipalMonitoring && (
                          <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
                            <div className="flex flex-wrap gap-2">
                              {municipalSources.map((source: any, idx: number) => (
                                <Badge key={idx} variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                                  {source.municipality_name || source.source_name || `Municipality ${source.municipality_id}`}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-neutral-500">
                  <Tag className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
                  <p>No topics configured yet</p>
                  <p className="text-sm mt-1">Create topics to monitor specific categories</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
      {/* Add Municipality Dialog */}
      <Dialog open={isAddMunicipalityOpen} onOpenChange={resetModal}>
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col !grid-rows-none">
          <AnimatePresence mode="wait">
            {addStep === "select" && (
              <motion.div 
                key="select"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col h-full"
              >
                <DialogHeader className="p-6 pb-4 border-b border-neutral-100 dark:border-neutral-800">
                  <DialogTitle>Add Municipality</DialogTitle>
                  <DialogDescription>
                    Select a municipality to add to your monitoring coverage.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="p-6 space-y-4">
                  {isLoadingMunicipalities ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-neutral-900 dark:text-white">
                          Municipality
                        </label>
                        <DropdownMenu open={municipalityDropdownOpen} onOpenChange={setMunicipalityDropdownOpen}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between text-left font-normal bg-white dark:bg-neutral-900"
                            >
                              <span>
                                {selectedMunicipalityIds.size > 0 
                                  ? `${selectedMunicipalityIds.size} municipality${selectedMunicipalityIds.size !== 1 ? 'ies' : ''} selected`
                                  : "Select municipalities..."}
                              </span>
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent 
                            className="min-w-[var(--radix-dropdown-menu-trigger-width)] w-[var(--radix-dropdown-menu-trigger-width)] max-h-[300px] overflow-y-auto"
                            side="bottom"
                            align="start"
                            sideOffset={4}
                          >
                            {isLoadingMunicipalities ? (
                              <div className="px-2 py-1.5 text-sm text-neutral-500">
                                Loading municipalities...
                              </div>
                            ) : availableMunicipalities.length > 0 ? (
                              availableMunicipalities.map((mun: any) => {
                                const isSelected = selectedMunicipalityIds.has(mun.id);
                                const currentMax = subscriptionTier === 'solo' ? 5 : 10;
                                const currentCount = municipalities.length;
                                const wouldExceedLimit = subscriptionTier && !isSelected && (currentCount + selectedMunicipalityIds.size >= currentMax);
                                
                                return (
                                  <DropdownMenuCheckboxItem
                                    key={mun.id}
                                    checked={isSelected}
                                    disabled={wouldExceedLimit}
                                    onCheckedChange={() => handleMunicipalityToggle(mun.id)}
                                    onSelect={(e) => e.preventDefault()}
                                    className="relative pr-8 pl-2 [&>span:first-child]:hidden"
                                  >
                                    <span className="flex-1">{mun.name}</span>
                                    {isSelected && (
                                      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                        <Check className="h-4 w-4 text-black dark:text-white" />
                                      </span>
                                    )}
                                  </DropdownMenuCheckboxItem>
                                );
                              })
                            ) : allMunicipalities && allMunicipalities.length > 0 ? (
                              <div className="px-2 py-1.5 text-sm text-neutral-500">
                                All municipalities are already being monitored
                              </div>
                            ) : (
                              <div className="px-2 py-1.5 text-sm text-neutral-500">
                                No municipalities available
                              </div>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {selectedMunicipalitiesList.length > 0 && (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {selectedMunicipalitiesList.map((mun: any) => (
                            <div key={mun.id} className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-800">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <MapPin className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                  <div className="font-semibold text-sm text-neutral-900 dark:text-white">
                                    {mun.name}
                                  </div>
                                  <div className="text-xs text-neutral-500">Ontario, Canada</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <DialogFooter className="p-6 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                  <Button variant="outline" onClick={resetModal}>Cancel</Button>
                  {subscriptionTier && selectedMunicipalityIds.size > 0 && (
                    <div className="flex items-center gap-2 text-xs text-neutral-500 mr-auto">
                      {currentMunicipalityCount + selectedMunicipalityIds.size > maxMunicipalities ? (
                        <span className="text-red-600 font-medium">
                          ⚠️ Will exceed {subscriptionTier === 'solo' ? 'Solo' : 'Org'} plan limit ({maxMunicipalities})
                        </span>
                      ) : (
                        <span>
                          {remainingSlots - selectedMunicipalityIds.size} slot{remainingSlots - selectedMunicipalityIds.size !== 1 ? 's' : ''} remaining
                        </span>
                      )}
                    </div>
                  )}
                  <Button 
                    className="bg-primary hover:bg-primary/90 text-white gap-2"
                    disabled={selectedMunicipalityIds.size === 0}
                    onClick={() => {
                      if (selectedMunicipalityIds.size === 0) return;
                      
                      // Recalculate values to ensure we have the latest subscription tier
                      const currentMax = subscriptionTier === 'solo' ? 5 : 10;
                      const currentCount = municipalities.length;
                      const newCount = currentCount + selectedMunicipalityIds.size;
                      
                      // Double-check limit before proceeding
                      if (subscriptionTier && newCount > currentMax) {
                        if (subscriptionTier === 'solo') {
                          setShowUpgradeDialog(true);
                        } else {
                          toast({
                            title: "Limit reached",
                            description: `You can monitor up to ${currentMax} municipalities on the Org plan.`,
                            variant: "destructive",
                          });
                        }
                        return;
                      }
                      setAddStep("confirm");
                    }}
                  >
                    Continue <ChevronRight className="h-4 w-4" />
                  </Button>
                </DialogFooter>
              </motion.div>
            )}

            {addStep === "confirm" && selectedMunicipalities.length > 0 && (
              <motion.div 
                key="confirm"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex flex-col h-full max-h-[calc(90vh-2rem)]"
              >
                <DialogHeader className="p-6 pb-4 border-b border-neutral-100 dark:border-neutral-800 flex-shrink-0">
                  <DialogTitle>Confirm Subscription</DialogTitle>
                  <DialogDescription>
                    Review your new subscription details below.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0 flex-shrink">
                  {/* Payment form removed - Brightstone internal use only */}
                  {/* Show municipalities overview */}
                  (<>
                      <div className="bg-neutral-50 dark:bg-neutral-900/50 p-4 rounded-xl border border-neutral-100 dark:border-neutral-800">
                        <div className="max-h-[180px] overflow-y-auto space-y-3 pr-2">
                          {selectedMunicipalities.map((mun: any) => (
                            <div key={mun.id} className="flex items-center justify-between min-h-[56px] py-1">
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div className="h-10 w-10 rounded-lg bg-white dark:bg-neutral-800 shadow-sm flex items-center justify-center text-primary flex-shrink-0">
                                  <Building2 className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h4 className="font-bold text-neutral-900 dark:text-white text-sm break-words">{mun.name}</h4>
                                  <p className="text-xs text-neutral-500 whitespace-nowrap">Municipality</p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0 ml-4">
                                {trialStatus?.is_trial_active ? (
                                  <>
                                    <div className="font-bold text-sm text-green-600 dark:text-green-400 whitespace-nowrap">
                                      Free
                                    </div>
                                    <div className="text-xs text-neutral-500 whitespace-nowrap">During trial</div>
                                  </>
                                ) : (
                                  <>
                                    <div className="font-bold text-sm text-neutral-900 dark:text-white whitespace-nowrap">
                                      {municipalityPriceData?.price_cad ? `$${municipalityPriceData.price_cad.toFixed(2)}` : '—'}
                                    </div>
                                    <div className="text-xs text-neutral-500 whitespace-nowrap">/month</div>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {selectedMunicipalities.length > 3 && (
                          <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700 text-xs text-center text-neutral-500">
                            {selectedMunicipalities.length - 3} more municipalit{selectedMunicipalities.length - 3 === 1 ? 'y' : 'ies'} in list
                          </div>
                        )}
                        {selectedMunicipalities.length > 0 && trialStatus?.is_trial_active && trialStatus.days_remaining !== undefined && (
                          <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3.5 space-y-2.5">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">🎉</span>
                                  <div>
                                    <div className="font-semibold text-sm text-green-700 dark:text-green-400">
                                      Free Trial Active
                                    </div>
                                    <div className="text-xs text-green-600 dark:text-green-500">
                                      {trialStatus.days_remaining} {trialStatus.days_remaining === 1 ? 'day' : 'days'} remaining
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs text-green-600 dark:text-green-500 font-medium">Trial ends</div>
                                  <div className="text-xs text-green-700 dark:text-green-400 font-semibold">
                                    {trialStatus.trial_end_date ? new Date(trialStatus.trial_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                  </div>
                                </div>
                              </div>
                              {municipalityPriceData?.price_cad && (
                                <div className="pt-2 border-t border-green-200 dark:border-green-800 flex items-center justify-between">
                                  <span className="text-xs text-neutral-600 dark:text-neutral-400">After trial:</span>
                                  <span className="text-sm font-bold text-neutral-900 dark:text-white">
                                    ${(municipalityPriceData.price_cad * selectedMunicipalities.length).toFixed(2)}/month
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {selectedMunicipalities.length > 0 && !trialStatus?.is_trial_active && municipalityPriceData?.price_cad && (
                          <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-between items-center">
                            <span className="font-bold text-sm text-neutral-900 dark:text-white">Total Monthly Cost</span>
                            <span className="font-bold text-lg text-primary">
                              ${(municipalityPriceData.price_cad * selectedMunicipalities.length).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Payment method section removed - Brightstone internal use only */}
                    </>)
                </div>
                {(
                  <DialogFooter className="p-6 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex justify-between flex-shrink-0">
                    <Button variant="ghost" onClick={() => setAddStep("select")} className="text-neutral-500">
                      Back
                    </Button>
                    <Button 
                      className="bg-primary hover:bg-primary/90 text-white gap-2 min-w-[140px]"
                      onClick={handleConfirmSubscription}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Processing...
                        </>
                      ) : (
                        <>
                          Confirm
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                )}
              </motion.div>
            )}

            {addStep === "success" && (
              <motion.div 
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col h-full items-center justify-center p-8 text-center min-h-[400px]"
              >
                <div className="h-20 w-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-300">
                  <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">Subscription Active!</h2>
                <p className="text-neutral-500 dark:text-neutral-400 max-w-xs mx-auto mb-4 leading-relaxed">
                  You have successfully added <strong>{selectedMunicipalities.length} {selectedMunicipalities.length === 1 ? 'municipality' : 'municipalities'}</strong> to your monitoring list. 
                  We'll start tracking immediately.
                </p>
                {trialStatus?.is_trial_active && (
                  <div className="max-w-xs mx-auto mb-6 px-4 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-xs text-green-700 dark:text-green-400 font-medium">
                      🎉 No charge during your free trial
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                      Trial ends {trialStatus.trial_end_date ? new Date(trialStatus.trial_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                    </p>
                  </div>
                )}
                <div className="w-full max-w-xs space-y-3">
                  <Link to="/add-property">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white">
                      Add Properties
                    </Button>
                  </Link>
                  <Button variant="outline" className="w-full" onClick={resetModal}>
                    Done
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
      
      {/* Upgrade Dialog */}
      <AlertDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade to Org Plan</AlertDialogTitle>
            <AlertDialogDescription>
              You've reached the Solo plan limit of 5 municipalities. Upgrade to the Org plan to monitor up to 10 municipalities.
              <br /><br />
              This will perform a prorated charge to upgrade your subscription.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpgradeToOrg}
              disabled={isUpgrading}
              className="bg-primary hover:bg-primary/90"
            >
              {isUpgrading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Upgrading...
                </>
              ) : (
                "Upgrade to Org Plan"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Switch Municipality Dialog */}
      <Dialog open={isSwitchDialogOpen} onOpenChange={(open) => !open && resetSwitchDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" />
              Switch Municipality
            </DialogTitle>
            <DialogDescription>
              You're at your plan limit ({currentMunicipalityCount}/{maxMunicipalities}). 
              Select a municipality to replace with a new one.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Select municipality to remove (if not pre-selected) */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Municipality to remove:
              </label>
              {!switchFromMunicipality ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-lg p-2">
                  {municipalities.filter((m: any) => m.properties === 0).length > 0 ? (
                    municipalities.filter((m: any) => m.properties === 0).map((city: any) => (
                      <div
                        key={city.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                          switchFromMunicipality?.id === city.id 
                            ? "bg-primary/10 border-2 border-primary" 
                            : "bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 border-2 border-transparent"
                        )}
                        onClick={() => setSwitchFromMunicipality(city)}
                      >
                        <div className="h-8 w-8 rounded-lg bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                          <MapPin className="h-4 w-4 text-neutral-500" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{city.name}</div>
                          <div className="text-xs text-neutral-500">0 properties</div>
                        </div>
                        {switchFromMunicipality?.id === city.id && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-sm text-neutral-500">
                      <Building2 className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                      All municipalities have properties. Remove properties first to switch.
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <div className="h-8 w-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-red-700 dark:text-red-400">{switchFromMunicipality.name}</div>
                    <div className="text-xs text-red-500">Will be deactivated</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setSwitchFromMunicipality(null)}
                  >
                    Change
                  </Button>
                </div>
              )}
            </div>

            {/* Step 2: Select new municipality */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Replace with:
              </label>
              <Select
                value={switchToMunicipalityId?.toString() || ""}
                onValueChange={(value) => setSwitchToMunicipalityId(parseInt(value))}
                disabled={!switchFromMunicipality}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a new municipality..." />
                </SelectTrigger>
                <SelectContent>
                  {availableMunicipalities.map((muni: any) => (
                    <SelectItem key={muni.id} value={muni.id.toString()}>
                      {muni.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {switchToMunicipalityId && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <div className="h-8 w-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-green-700 dark:text-green-400">
                      {availableMunicipalities.find((m: any) => m.id === switchToMunicipalityId)?.name}
                    </div>
                    <div className="text-xs text-green-500">Will be added to monitoring</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetSwitchDialog}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-white gap-2"
              onClick={handleConfirmSwitch}
              disabled={!switchFromMunicipality || !switchToMunicipalityId || isSwitching}
            >
              {isSwitching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Switching...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="h-4 w-4" /> Switch Municipality
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Category Monitoring Confirmation Dialog */}
      <Dialog open={isCustomCategoryDialogOpen} onOpenChange={(open) => {
        if (!open && !isAddingCategoryMonitoring) {
          setIsCustomCategoryDialogOpen(false);
          setPendingCustomCategories([]);
          setSelectedCustomCategoryIds(new Set());
          setNewMunicipalityForCategories(null);
        }
      }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              Update Custom Topics
            </DialogTitle>
            <DialogDescription>
              Select which topics you'd like monitoring <strong>{newMunicipalityForCategories?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {pendingCustomCategories.length} custom topic(s) found
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={selectAllCustomCategories}
                  className="text-xs h-7"
                >
                  Select All
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={deselectAllCustomCategories}
                  className="text-xs h-7"
                >
                  Clear
                </Button>
              </div>
            </div>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {pendingCustomCategories.map((category) => (
                <div
                  key={category.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedCustomCategoryIds.has(category.id)
                      ? 'bg-primary/10 border-primary'
                      : 'bg-muted/50 border-muted-foreground/20 hover:border-muted-foreground/40'
                  }`}
                  onClick={() => toggleCustomCategorySelection(category.id)}
                >
                  <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                    selectedCustomCategoryIds.has(category.id)
                      ? 'bg-primary border-primary'
                      : 'border-muted-foreground/40'
                  }`}>
                    {selectedCustomCategoryIds.has(category.id) && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{category.display_name}</div>
                    <div className="text-xs text-muted-foreground">Custom Topic</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {/* Skip button - only shown when no topics selected */}
            {selectedCustomCategoryIds.size === 0 && (
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsCustomCategoryDialogOpen(false);
                  setPendingCustomCategories([]);
                  setSelectedCustomCategoryIds(new Set());
                  setNewMunicipalityForCategories(null);
                }}
                disabled={isAddingCategoryMonitoring}
              >
                Skip
              </Button>
            )}
            <Button
              className={`gap-2 ${
                selectedCustomCategoryIds.size > 0 
                  ? 'bg-primary hover:bg-primary/90 text-white' 
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
              onClick={handleConfirmCategoryMonitoring}
              disabled={isAddingCategoryMonitoring || selectedCustomCategoryIds.size === 0}
            >
              {isAddingCategoryMonitoring ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Updating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" /> Continue
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}